import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Icon } from "@iconify/react";
import { NotificationType } from "@markit/common.types";
import { Colors } from "../../../../utils/colors";
import IntegrationPanelContainer from "./IntegrationPanelContainer";
import ConfirmActionModal from "../../../Containers/ConfirmPopups/ConfirmActionModal";
import AlertContainer from "../../../Containers/AlertContainer";
import RectangleButton from "../../../Buttons/RectangleButton";
import { HorizontalDivider } from "../../../Dividers/HorizontalDivider";
import { showNotificationBanner } from "../../../../utils/notificationUtils";
import {
  WhatsAppConnectionStatus,
  completeWhatsAppConnect,
  disconnectWhatsApp,
  getWhatsAppStatus,
  launchWhatsAppSignup,
  loadFacebookSdk,
  resubmitWhatsAppTemplate,
} from "../../../../utils/whatsappApiUtils";

const whatsAppDescription =
  "Send your messages to international subscribers over WhatsApp, still from your own Markit number.";

type AlertText = { header: string; subHeader: string };

const TRY_AGAIN = "Please try again.";

const WhatsAppIcon = (props: {
  size: number;
  padding: number;
  borderRadius?: number;
}) => (
  <div
    className="Centering"
    style={{
      borderRadius: props.borderRadius ?? 10,
      padding: props.padding,
      backgroundColor: Colors.WHITE,
      border: `1px solid ${Colors.GRAY4}`,
    }}
  >
    <Icon icon="logos:whatsapp-icon" height={props.size} />
  </div>
);

// What the card says about the connection, from the token and the template status
const getStatusDisplay = (
  status: Extract<WhatsAppConnectionStatus, { connected: true }>
) => {
  if (status.tokenError) {
    return { label: "Reconnect needed", isError: true };
  }
  if (!status.template) {
    return { label: "Template not submitted", isError: true };
  }
  switch (status.template.status) {
    case "APPROVED":
      return { label: "Connected", isError: false };
    case "PENDING":
      return { label: "Template in review", isError: false };
    case "REJECTED":
      return { label: "Template rejected", isError: true };
    default:
      return { label: `Template ${status.template.status}`, isError: true };
  }
};

// TEMP(MARK-3954): admin-only while WhatsApp is a test build (see IntegrationsPanel).
// Reads its state from the backend, which asks Meta, so it updates on load, after an
// action, and from Refresh.
export const WhatsAppPanelSection = () => {
  const dispatch = useDispatch();
  const [status, setStatus] = useState<WhatsAppConnectionStatus>();
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [loadingConnect, setLoadingConnect] = useState(false);
  const [loadingResubmit, setLoadingResubmit] = useState(false);
  const [loadingDisconnect, setLoadingDisconnect] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [alertText, setAlertText] = useState<AlertText>({
    header: "",
    subHeader: "",
  });

  const refreshStatus = useCallback(async () => {
    setLoadingStatus(true);
    try {
      setStatus(await getWhatsAppStatus());
    } catch (error) {
      console.error(error);
      setAlertText({
        header: "Couldn't load your WhatsApp connection.",
        subHeader: TRY_AGAIN,
      });
    }
    setLoadingStatus(false);
  }, []);

  useEffect(() => {
    refreshStatus();
    // Loaded ahead so the Connect press can open Meta's popup directly
    loadFacebookSdk().catch((error) => console.error(error));
  }, [refreshStatus]);

  const connectOnPress = useCallback(async () => {
    setLoadingConnect(true);
    try {
      const result = await launchWhatsAppSignup();
      setStatus(await completeWhatsAppConnect(result));
      showNotificationBanner(
        dispatch,
        "WhatsApp connected",
        NotificationType.AFFIRMATIVE
      );
    } catch (error) {
      console.error(error);
      if ((error as Error)?.message !== "cancelled") {
        setAlertText({
          header: "Something went wrong connecting WhatsApp.",
          subHeader: TRY_AGAIN,
        });
      }
    }
    setLoadingConnect(false);
  }, [dispatch]);

  const resubmitOnPress = useCallback(async () => {
    setLoadingResubmit(true);
    try {
      await resubmitWhatsAppTemplate();
      await refreshStatus();
    } catch (error) {
      console.error(error);
      setAlertText({
        header: "Something went wrong submitting your message template.",
        subHeader: TRY_AGAIN,
      });
    }
    setLoadingResubmit(false);
  }, [refreshStatus]);

  const disconnectOnPress = useCallback(async () => {
    setLoadingDisconnect(true);
    try {
      await disconnectWhatsApp();
      setStatus({ connected: false });
      showNotificationBanner(
        dispatch,
        "Disconnected",
        NotificationType.NEGATIVE
      );
    } catch (error) {
      console.error(error);
      setAlertText({
        header: "Something went wrong disconnecting WhatsApp.",
        subHeader: TRY_AGAIN,
      });
    }
    setLoadingDisconnect(false);
    setShowDisconnectModal(false);
  }, [dispatch]);

  const renderConnectedContent = () => {
    if (!status?.connected) {
      return null;
    }
    const statusDisplay = getStatusDisplay(status);
    const template = status.template;
    const canResubmit =
      !status.tokenError && (!template || template.status === "REJECTED");

    return (
      <div className="ColumnNormal" style={{ height: "100%", width: "100%" }}>
        <div className="AlignedRow" style={{ gap: 14, padding: 14 }}>
          <WhatsAppIcon size={34} padding={10} />
          <div className="ColumnNormal" style={{ gap: 7 }}>
            <div className="AlignedRow" style={{ gap: 10 }}>
              <span className="sectionTitle">WhatsApp</span>
              <span
                className="bodySubtext"
                style={
                  statusDisplay.isError ? { color: Colors.RED3 } : undefined
                }
              >
                {statusDisplay.label}
              </span>
            </div>
            <span className="smallBodySubtext">{whatsAppDescription}</span>
          </div>
        </div>
        <HorizontalDivider />
        <div
          className="ColumnNormal"
          style={{
            padding: 14,
            gap: 14,
            flexGrow: 1,
            justifyContent: "space-between",
          }}
        >
          <div className="ColumnNormal" style={{ gap: 10 }}>
            {status.tokenError ? (
              <span className="smallBodySubtext" style={{ color: Colors.RED3 }}>
                Markit no longer has access to your WhatsApp account. Reconnect
                to keep sending.
              </span>
            ) : (
              <>
                <span className="smallBodySubtext">
                  Name: {status.displayName}
                </span>
                <span className="smallBodySubtext">
                  Number: {status.displayPhoneNumber}
                </span>
                <span className="smallBodySubtext">
                  Message template:{" "}
                  {template
                    ? template.status === "PENDING"
                      ? "In review with Meta"
                      : template.status.toLowerCase()
                    : "Not submitted"}
                </span>
                {template?.rejectedReason ? (
                  <span
                    className="smallBodySubtext"
                    style={{ color: Colors.RED3 }}
                  >
                    Meta&apos;s reason: {template.rejectedReason}
                  </span>
                ) : null}
                <span className="smallBodySubtext">
                  Add a payment method in WhatsApp Manager before sending.
                </span>
              </>
            )}
          </div>
          <div className="AlignedRow" style={{ gap: 10, flexWrap: "wrap" }}>
            <RectangleButton
              buttonLabel={
                <span style={{ fontSize: 14, fontWeight: 400 }}>
                  Disconnect
                </span>
              }
              onPress={() => setShowDisconnectModal(true)}
              altColor={Colors.WHITE1}
              altTextColor={Colors.RED3}
              altPaddingHorz={14}
              altPaddingVert={10}
              altBorderRadius={8}
              width={106}
              containerStyles={{ border: `0.5px solid ${Colors.GRAY2}` }}
            />
            <RectangleButton
              buttonLabel={
                <span style={{ fontSize: 14, fontWeight: 400 }}>Refresh</span>
              }
              onPress={refreshStatus}
              altColor={Colors.WHITE1}
              altTextColor={Colors.BLACK}
              altPaddingHorz={14}
              altPaddingVert={10}
              altBorderRadius={8}
              width={106}
              disabled={loadingStatus}
              loading={loadingStatus}
              containerStyles={{ border: `0.5px solid ${Colors.GRAY2}` }}
            />
            {status.tokenError ? (
              <RectangleButton
                buttonLabel={
                  <span style={{ fontSize: 14, fontWeight: 400 }}>
                    Reconnect
                  </span>
                }
                onPress={connectOnPress}
                altColor={Colors.BLACK}
                altTextColor={Colors.WHITE}
                altPaddingHorz={14}
                altPaddingVert={10}
                altBorderRadius={8}
                disabled={loadingConnect}
                loading={loadingConnect}
              />
            ) : canResubmit ? (
              <RectangleButton
                buttonLabel={
                  <span style={{ fontSize: 14, fontWeight: 400 }}>
                    {template ? "Resubmit template" : "Submit template"}
                  </span>
                }
                onPress={resubmitOnPress}
                altColor={Colors.BLACK}
                altTextColor={Colors.WHITE}
                altPaddingHorz={14}
                altPaddingVert={10}
                altBorderRadius={8}
                disabled={loadingResubmit}
                loading={loadingResubmit}
              />
            ) : null}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <IntegrationPanelContainer
        isConnected={!!status?.connected}
        connectedContent={renderConnectedContent()}
        title="WhatsApp Integration"
        description={whatsAppDescription}
        actionLabel="Connect"
        onActionPress={connectOnPress}
        actionLoading={loadingConnect || (loadingStatus && !status)}
        actionDisabled={loadingConnect || loadingStatus}
        icon={<WhatsAppIcon size={57} padding={22} borderRadius={15} />}
      />
      <ConfirmActionModal
        heading="Are you sure you want to disconnect WhatsApp from Markit?"
        subtext="Markit will stop sending from your WhatsApp number. The number stays on your WhatsApp Business account, and you can reconnect at any time."
        confirmButtonText="Disconnect"
        hideModal={!showDisconnectModal}
        setIsVisible={setShowDisconnectModal}
        confirmOnPress={disconnectOnPress}
        loadingConfirm={loadingDisconnect}
      />
      <AlertContainer
        headerComp={alertText.header}
        subHeaderComp={alertText.subHeader}
        closeModal={() => setAlertText({ header: "", subHeader: "" })}
        hideModal={alertText.header === ""}
      />
    </>
  );
};
